import { useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/shared/i18n";

/**
 * 해제 사유 고르기 — 경보창(AlarmCenter)과 경보 화면(AlertsPage)이 같이 쓴다(2026-10-19 · 10-12 검증 V-31).
 * 고르면 해제 요청에 실려 서버 원장 `clear_reason`·감사 로그에 남는다.
 * 보내는 글은 화면 언어와 상관없이 **한국어 문구**(ko 사전) — 원장을 읽는 관리자 기준. 「기타」는 적은 그대로.
 *
 * 한 건 해제는 선택이다. **여러 건을 한꺼번에 해제할 때는 필수**다(2026-10-27 사용자 결정 —
 * SOP 2.4 「여러 경보를 한꺼번에 해제할 때는 사유를 기록한다」를 화면이 지키게). 기준은 {@link bulkClearNeedsReason}.
 */
export const CLEAR_REASONS = ["falseAlarm", "handled", "ventilated", "drill", "other"] as const;
export type ClearReasonCode = (typeof CLEAR_REASONS)[number] | "";
/** 「기타」 직접 입력 최대 길이(서버 한도 500 보다 짧게 — 원장 한 줄로 읽히게). */
export const CLEAR_REASON_TEXT_MAX = 200;

/** 전체 해제가 사유를 요구하는가 — 해제할 경보가 2건 이상일 때. 1건이면 한 건 해제와 같으므로 선택. */
export const bulkClearNeedsReason = (count: number) => count >= 2;

/** 고른 사유 상태 + 서버로 보낼 글(`reason`, 안 골랐거나 「기타」를 비웠으면 빈 글 = 본문 없이 종전 해제). */
export function useClearReason() {
  const [code, setCode] = useState<ClearReasonCode>("");
  const [text, setText] = useState("");
  const reason =
    code === "other"
      ? text.trim().slice(0, CLEAR_REASON_TEXT_MAX)
      : code
        ? i18n.getFixedT("ko")(`alarm.clearReason.${code}`)
        : "";
  const reset = () => {
    setCode("");
    setText("");
  };
  return { code, setCode, text, setText, reason, reset };
}

export function ClearReasonPicker({
  code,
  onCode,
  text,
  onText,
}: {
  code: ClearReasonCode;
  onCode: (c: ClearReasonCode) => void;
  text: string;
  onText: (s: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <select
        value={code}
        onChange={(e) => onCode(e.target.value as ClearReasonCode)}
        aria-label={t("alarm.clearReason.label")}
        title={t("alarm.clearReason.hint")}
        className="h-8 max-w-[200px] rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-elevated)] px-2 text-[12.5px] text-[var(--fg)] hover:border-[var(--border-strong)] focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
      >
        <option value="">{t("alarm.clearReason.none")}</option>
        {CLEAR_REASONS.map((c) => (
          <option key={c} value={c}>
            {t(`alarm.clearReason.${c}`)}
          </option>
        ))}
      </select>
      {code === "other" && (
        <input
          type="text"
          value={text}
          onChange={(e) => onText(e.target.value)}
          maxLength={CLEAR_REASON_TEXT_MAX}
          placeholder={t("alarm.clearReason.placeholder")}
          aria-label={t("alarm.clearReason.placeholder")}
          className="h-8 w-[180px] rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-elevated)] px-2 text-[12.5px] text-[var(--fg)] placeholder:text-[var(--fg-subtle)] focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
        />
      )}
    </div>
  );
}
